import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import '@/App.css';
import '@/lib/auth'; // installs axios interceptors
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import PreviewBanner from '@/components/PreviewBanner';
import BrandHead from '@/components/BrandHead';
import Hreflang from '@/components/Hreflang';
import SmoothScroll from '@/components/SmoothScroll';
import ScrollToTop from '@/components/ScrollToTop';
import GA4Tracker from '@/components/GA4Tracker';
import ScrollLag from '@/components/ScrollLag';
import HeroParallax from '@/components/HeroParallax';
import MediaParallax from '@/components/MediaParallax';
import ImageProtection from '@/components/ImageProtection';
import { LanguageProvider, FRENCH_PUBLIC_ENABLED } from '@/lib/i18n';
import Home from '@/pages/Home';
import Artists from '@/pages/Artists';
import ArtistDetail from '@/pages/ArtistDetail';
import Artworks from '@/pages/Artworks';
import ArtworkDetail from '@/pages/ArtworkDetail';
import Exhibitions from '@/pages/Exhibitions';
import ExhibitionDetail from '@/pages/ExhibitionDetail';
import About from '@/pages/About';
import Journal from '@/pages/Journal';
import JournalDetail from '@/pages/JournalDetail';
import Contact from '@/pages/Contact';
import PrivateViewing from '@/pages/PrivateViewing';
import PaymentSuccess from '@/pages/PaymentSuccess';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import { CollectionList, CollectionEditor } from '@/pages/admin/Collection';
import { AdminEnquiries, AdminNewsletter } from '@/pages/admin/AdminInbox';
import SiteSettings from '@/pages/admin/SiteSettings';
import Account from '@/pages/admin/Account';
import Videos from '@/pages/Videos';
import VideoDetail from '@/pages/VideoDetail';

function PublicChrome({ children }) {
    const loc = useLocation();
    const isAdmin = loc.pathname.startsWith('/admin');
    if (isAdmin) return children;
    return (
        <SmoothScroll>
            <ImageProtection />
            <a href="#main-content" className="skip-link" data-testid="skip-to-content">Skip to content</a>
            <Nav />
            {children}
            <Footer />
            <PreviewBanner />
        </SmoothScroll>
    );
}

function App() {
    /**
     * PUBLIC_ROUTES — single source of truth for public routes.
     * Rendered twice: once at the root (English canonical URLs) and once
     * under /fr/* for French. Phase 1: French routes render the same
     * components; strings still fall back to English until Phase 3.
     */
    const PUBLIC_ROUTES = [
        { path: '/', element: <Home /> },
        { path: '/artists', element: <Artists /> },
        { path: '/artists/:slug', element: <ArtistDetail /> },
        { path: '/artworks', element: <Artworks /> },
        { path: '/artworks/:slug', element: <ArtworkDetail /> },
        { path: '/exhibitions', element: <Exhibitions /> },
        { path: '/exhibitions/:slug', element: <ExhibitionDetail /> },
        { path: '/about', element: <About /> },
        { path: '/journal', element: <Journal /> },
        { path: '/journal/:slug', element: <JournalDetail /> },
        { path: '/film', element: <Videos /> },
        { path: '/film/:slug', element: <VideoDetail /> },
        { path: '/contact', element: <Contact /> },
        { path: '/private-viewing', element: <PrivateViewing /> },
        { path: '/payment-success', element: <PaymentSuccess /> },
    ];

    return (
        <div className="App grain">
            <BrowserRouter>
                <LanguageProvider>
                    <ScrollToTop />
                    <GA4Tracker />
                    <ScrollLag />
                    <HeroParallax />
                    <BrandHead />
                    <Hreflang />
                    <PublicChrome>
                        <main id="main-content">
                            <Routes>
                                {PUBLIC_ROUTES.map((r) => (
                                    <Route key={`en-${r.path}`} path={r.path} element={r.element} />
                                ))}
                                {FRENCH_PUBLIC_ENABLED
                                    ? PUBLIC_ROUTES.map((r) => (
                                          <Route
                                              key={`fr-${r.path}`}
                                              path={r.path === '/' ? '/fr' : `/fr${r.path}`}
                                              element={r.element}
                                          />
                                      ))
                                    : (
                                          <>
                                              {/* Flag OFF: any /fr or /fr/* request redirects to the
                                                 English homepage. No French metadata is ever served,
                                                 no /fr URL is indexable (see robots.txt Disallow and
                                                 sitemap emission conditions on the backend). */}
                                              <Route path="/fr" element={<Navigate to="/" replace />} />
                                              <Route path="/fr/*" element={<Navigate to="/" replace />} />
                                          </>
                                      )}

                                {/* Admin */}
                                <Route path="/admin/login" element={<AdminLogin />} />
                                <Route path="/admin" element={<AdminLayout />}>
                                    <Route index element={<AdminDashboard />} />
                                    <Route path="artists" element={<CollectionList entity="artists" />} />
                                    <Route path="artists/:slug" element={<CollectionEditor entity="artists" />} />
                                    <Route path="artworks" element={<CollectionList entity="artworks" />} />
                                    <Route path="artworks/:slug" element={<CollectionEditor entity="artworks" />} />
                                    <Route path="exhibitions" element={<CollectionList entity="exhibitions" />} />
                                    <Route path="exhibitions/:slug" element={<CollectionEditor entity="exhibitions" />} />
                                    <Route path="journal" element={<CollectionList entity="journal" />} />
                                    <Route path="journal/:slug" element={<CollectionEditor entity="journal" />} />
                                    <Route path="videos" element={<CollectionList entity="videos" />} />
                                    <Route path="videos/:slug" element={<CollectionEditor entity="videos" />} />
                                    <Route path="enquiries" element={<AdminEnquiries />} />
                                    <Route path="newsletter" element={<AdminNewsletter />} />
                                    <Route path="settings" element={<SiteSettings />} />
                                    <Route path="account" element={<Account />} />
                                </Route>
                            </Routes>
                        </main>
                    </PublicChrome>
                </LanguageProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
